<template>
  <Transition name="banner">
    <div v-if="showBanner" class="trial-banner" :class="`trial-banner--${bannerType}`">
      <div class="trial-banner__content">
        <div class="trial-banner__info">
          <div class="trial-banner__badge">
            <Icon :icon="bannerIcon" />
            <span>{{ daysLeft }} day{{ daysLeft === 1 ? "" : "s" }} left</span>
          </div>
          <div class="trial-banner__message">
            <span class="trial-banner__title">{{ bannerTitle }}</span>
            <span class="trial-banner__subtitle">{{ bannerSubtitle }}</span>
          </div>
        </div>
        <div class="trial-banner__actions">
          <Button
            variant="secondary"
            size="xs"
            :icon="ctaIcon"
            class="trial-banner__cta"
            @click="handleCTAClick"
          >
            {{ bannerCTA }}
          </Button>
          <button class="trial-banner__dismiss" @click="dismissBanner" aria-label="Dismiss">
            <Icon icon="mdi:close" />
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { Icon } from "@iconify/vue";
import Button from "@/components/ui/Button.vue";
import { useBillingStatus } from "@/composables/billing/useBillingStatus";
import {
  CREATE_SETUP_CHECKOUT,
  GET_SUBSCRIPTION_STATUS,
  GET_TRIAL_STATUS,
} from "@/store/action-types";

const DISMISS_KEY = "trialBannerDismissed";
const DISMISS_DURATION = 24 * 60 * 60 * 1000;

const store = useStore();
const router = useRouter();
const isDismissed = ref(false);

const isTrialing = computed(() => store.getters.isTrialing);
const hasPaymentMethod = computed(() => store.getters.hasPaymentMethod);
const isFreeTrialActive = computed(() => store.getters.isFreeTrialActive);
const trialStatus = computed(() => store.getters.trialStatus?.data);

const subscriptionStatus = computed(() => store.getters.subscriptionStatus?.data);

const { isUsageBlocked } = useBillingStatus();

const daysLeft = computed(() => {
  if (isFreeTrialActive.value && trialStatus.value?.daysRemaining !== undefined) {
    return trialStatus.value.daysRemaining;
  }
  return store.getters.daysLeftInTrial;
});

const bannerType = computed(() => {
  if (isUsageBlocked.value) return null;
  // Never show trial banner if user has an active subscription
  if (subscriptionStatus.value?.hasSubscription && subscriptionStatus.value?.status === "active") {
    return null;
  }
  if (isFreeTrialActive.value) return "free_trial";
  if (isTrialing.value && !hasPaymentMethod.value) return "stripe_trial";
  return null;
});

const showBanner = computed(() => {
  return bannerType.value !== null && !isDismissed.value && daysLeft.value > 0;
});

const bannerIcon = computed(() => {
  return bannerType.value === "free_trial" ? "mdi:rocket-outline" : "mdi:clock-outline";
});

const bannerTitle = computed(() => {
  return bannerType.value === "free_trial" ? "Free Trial" : "Trial Period";
});

const bannerSubtitle = computed(() => {
  return bannerType.value === "free_trial"
    ? "Upgrade for full access to calls, SMS, enrichment & more"
    : "Add a payment method to continue after your trial";
});

const bannerCTA = computed(() => {
  return bannerType.value === "free_trial" ? "Upgrade Now" : "Add Payment Method";
});

const ctaIcon = computed(() => {
  return bannerType.value === "free_trial"
    ? "mdi:arrow-up-circle-outline"
    : "mdi:credit-card-plus-outline";
});

const checkDismissed = () => {
  const dismissedAt = localStorage.getItem(DISMISS_KEY);
  if (dismissedAt) {
    const dismissedTime = parseInt(dismissedAt, 10);
    if (Date.now() - dismissedTime < DISMISS_DURATION) {
      isDismissed.value = true;
      return;
    }
    localStorage.removeItem(DISMISS_KEY);
  }
  isDismissed.value = false;
};

const dismissBanner = () => {
  localStorage.setItem(DISMISS_KEY, Date.now().toString());
  isDismissed.value = true;
};

const handleCTAClick = async () => {
  if (bannerType.value === "free_trial") {
    router.push("/subscription-wall");
  } else {
    sessionStorage.setItem("sb_payment_setup_return_path", window.location.pathname);
    const data = await store.dispatch(CREATE_SETUP_CHECKOUT, {
      returnPath: window.location.pathname,
    });
    if (data?.url) {
      window.location.href = data.url;
    }
  }
};

onMounted(() => {
  checkDismissed();
});

watch(
  () => store.getters.account?.data?.id,
  async (accountId, oldAccountId) => {
    if (accountId && !oldAccountId) {
      await Promise.all([
        store.dispatch(GET_SUBSCRIPTION_STATUS),
        store.dispatch(GET_TRIAL_STATUS),
      ]);
    }
  }
);

watch(
  () => store.getters.subscriptionStatus,
  () => {
    if (hasPaymentMethod.value) {
      localStorage.removeItem(DISMISS_KEY);
      isDismissed.value = false;
    }
  },
  { deep: true }
);
</script>

<style scoped>
.trial-banner {
  width: 100%;
  padding: 6px 24px;
  z-index: 1000;
}

.trial-banner--free_trial {
  background: #4f46e5;
}

.trial-banner--stripe_trial {
  background: #3b82f6;
}

.trial-banner__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 20px;
}

.trial-banner__info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.trial-banner__badge {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
}

.trial-banner__badge .iconify {
  font-size: 14px;
}

.trial-banner__message {
  display: flex;
  align-items: center;
  gap: 8px;
}

.trial-banner__title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #fff;
}

.trial-banner__subtitle {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.9);
}

.trial-banner__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.trial-banner__cta {
  background: #fff !important;
  color: #4f46e5 !important;
  border-color: #fff !important;
  font-size: 0.75rem !important;
  padding: 4px 10px !important;
  min-height: 28px !important;
  height: 28px !important;
}

.trial-banner__cta :deep(.iconify) {
  font-size: 14px;
}

.trial-banner--stripe_trial .trial-banner__cta {
  color: #3b82f6 !important;
}

.trial-banner__cta:hover {
  background: #f8f8ff !important;
}

.trial-banner__dismiss {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: #fff;
  transition: background-color 0.2s ease;
}

.trial-banner__dismiss:hover {
  background: rgba(255, 255, 255, 0.25);
}

.trial-banner__dismiss .iconify {
  font-size: 14px;
}

.banner-enter-active,
.banner-leave-active {
  transition: all 0.3s ease;
}

.banner-enter-from,
.banner-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}

@media (max-width: 900px) {
  .trial-banner__message {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
}

@media (max-width: 768px) {
  .trial-banner {
    padding: 8px 16px;
  }

  .trial-banner__content {
    flex-direction: column;
    gap: 12px;
  }

  .trial-banner__info {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .trial-banner__actions {
    width: 100%;
    justify-content: space-between;
  }

  .trial-banner__cta {
    flex: 1;
    justify-content: center;
  }
}
</style>
